// The KillSwitch console. Three write paths, deliberately separated and
// ordered by consequence: raise the budget (routine), trip now (stops all
// new spend immediately), reset with a fresh budget (clears a trip). Both
// dangerous actions confirm first, and the trip button is red in a page
// where nothing else is -- there is one destructive control here and it
// should look like one.
import { useCallback, useEffect, useState } from "react";
import { fetchKillSwitchEvents, fetchKillSwitchState, type KillSwitchEvent, type KillSwitchState } from "../../api/admin";
import { proxyKillswitch } from "../../api/client";
import { formatMinor, formatWhen } from "../../lib/format";
import { ageLabel, ageSeconds, budgetUsedFraction, fundsTotal, KILLSWITCH_STALE_S, providerSpend } from "../../lib/adminStatus";

/** Inputs reserved for later (06-killswitch.md §1): always 0 in V1, so they
 * are hidden while they are 0 rather than read as a measurement. */
const RESERVED_INPUTS = new Set(["idle_earned_total_minor", "vast_spent_total_minor", "last_vast_at"]);

const REFRESH_MS = 10000;

export default function AdminKillSwitch() {
  const [state, setState] = useState<KillSwitchState | null>(null);
  const [events, setEvents] = useState<KillSwitchEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [budget, setBudget] = useState("");
  const [tripReason, setTripReason] = useState("");
  const [resetBudget, setResetBudget] = useState("");

  const load = useCallback(async () => {
    try {
      setState(await fetchKillSwitchState());
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to load KillSwitch state");
    }
    try {
      setEvents(await fetchKillSwitchEvents(20));
    } catch {
      setEvents(null);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  async function onSetBudget() {
    const minor = Math.round(Number(budget) * 100);
    if (!Number.isFinite(minor) || minor <= 0) {
      setError("enter a positive dollar budget");
      return;
    }
    setError(null);
    try {
      await proxyKillswitch("budget", { method: "POST", body: JSON.stringify({ budget_total_minor: minor }) });
      setBudget("");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to set budget");
    }
  }

  async function onTrip() {
    if (!confirm("Trip KillSwitch now? This stops all new spend immediately.")) return;
    setError(null);
    try {
      await proxyKillswitch("trip", {
        method: "POST",
        body: JSON.stringify({ reason: tripReason || "manual trip from admin UI" }),
      });
      setTripReason("");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to trip KillSwitch");
    }
  }

  async function onReset() {
    const minor = Math.round(Number(resetBudget) * 100);
    if (!Number.isFinite(minor) || minor <= 0) {
      setError("enter a positive dollar budget to reset with");
      return;
    }
    if (!confirm(`Reset KillSwitch with a budget of $${(minor / 100).toFixed(2)}? This clears the tripped state.`)) return;
    setError(null);
    try {
      await proxyKillswitch("reset", { method: "POST", body: JSON.stringify({ budget_total_minor: minor }) });
      setResetBudget("");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to reset KillSwitch");
    }
  }

  const inputs = state?.inputs;
  const used = budgetUsedFraction(state);
  const spent = providerSpend(state) ?? 0;
  const funds = fundsTotal(state);
  const inputsAge = ageSeconds(inputs?.last_runpod_at);
  const stale = inputsAge === undefined || inputsAge > KILLSWITCH_STALE_S;
  const runpod = state?.providers?.runpod;
  const live = state?.live_resources?.runpod;
  const vast = state?.providers?.vast;
  const vastLive = state?.live_resources?.vast;

  return (
    <>
      <div className="a-head">
        <div>
          <h1 className="a-h1">KillSwitch</h1>
          <p className="a-lede">
            The budget ceiling and the switch under it. Armed means spend is allowed up to the
            budget; tripped means every new task is refused, immediately and without exception.
          </p>
        </div>
        {state && (
          <span className={`a-state a-state--${state.tripped ? "tripped" : "running"}`}>
            {state.tripped ? "tripped" : "armed"}
          </span>
        )}
      </div>

      {error && <p className="a-error">{error}</p>}

      {state === null ? (
        <p className="a-loading">Loading…</p>
      ) : (
        <>
          <div className="a-tiles">
            <div className="a-tile">
              <span className="a-tile__label">State</span>
              <span className={state.tripped ? "a-tile__value a-tile__value--red" : "a-tile__value a-tile__value--green"}>
                {state.tripped ? "TRIPPED" : "ARMED"}
              </span>
              <span className="a-tile__sub">{state.tripped_at ? formatWhen(state.tripped_at) : "spend allowed"}</span>
            </div>
            <div className="a-tile">
              <span className="a-tile__label">Balance</span>
              <span className="a-tile__value a-tile__value--cyan">
                {state.balance_minor !== undefined ? formatMinor(state.balance_minor, "USD_CENTS") : "—"}
              </span>
              <span className={"a-tile__sub" + (stale ? " a-tile__sub--warn" : "")}>
                budget + billed − provider spend · {stale ? "stale, " : ""}
                {ageLabel(inputsAge)}
                {state.tripped ? " (frozen while tripped)" : ""}
              </span>
            </div>
            <div className="a-tile">
              <span className="a-tile__label">Budget</span>
              <span className="a-tile__value">
                {inputs?.budget_total_minor !== undefined ? formatMinor(inputs.budget_total_minor, "USD_CENTS") : "—"}
              </span>
              {used !== undefined && (
                <span className={`a-gauge a-gauge--${used >= 0.9 ? "red" : used >= 0.7 ? "amber" : "green"}`}>
                  <span style={{ width: `${Math.round(used * 100)}%` }} />
                </span>
              )}
              <span className="a-tile__sub">
                {used !== undefined && funds !== undefined
                  ? `${Math.round(used * 100)}% of ${formatMinor(funds, "USD_CENTS")} funds spent (budget + billed)`
                  : "no budget set"}
              </span>
            </div>
            <div className="a-tile">
              <span className="a-tile__label">Provider spend</span>
              <span className="a-tile__value a-tile__value--amber">{formatMinor(spent, "USD_CENTS")}</span>
              <span className="a-tile__sub">
                RunPod, measured from the account balance
                {(inputs?.vast_spent_total_minor ?? 0) > 0 ? ` · vast ${formatMinor(inputs?.vast_spent_total_minor ?? 0, "USD_CENTS")}` : ""}
              </span>
            </div>
            <div className="a-tile">
              <span className="a-tile__label">RunPod now</span>
              <span className="a-tile__value">{runpod ? `${formatMinor(runpod.spend_per_hr_minor, "USD_CENTS")}/h` : "—"}</span>
              <span className="a-tile__sub">
                {runpod ? `balance ${formatMinor(runpod.balance_minor, "USD_CENTS")} · ${ageLabel(ageSeconds(runpod.read_at))}` : "no balance read yet"}
              </span>
            </div>
            <div className="a-tile">
              <span className="a-tile__label">Live at RunPod</span>
              <span className={"a-tile__value" + (live ? " a-tile__value--amber" : "")}>{live === undefined || live === null ? "—" : live}</span>
              <span className="a-tile__sub">our pods + running serverless workers; KillSwitch does not stop these</span>
            </div>
            {vast && (
              <div className="a-tile">
                <span className="a-tile__label">Vast now</span>
                <span className={"a-tile__value" + (vastLive ? " a-tile__value--amber" : "")}>
                  {vastLive === undefined || vastLive === null ? "—" : vastLive}
                </span>
                <span className="a-tile__sub">
                  instances labelled ours · credit {formatMinor(vast.balance_minor, "USD_CENTS")} · {ageLabel(ageSeconds(vast.read_at))}
                </span>
              </div>
            )}
          </div>

          {state.reason && <p className="a-error">tripped: {state.reason}</p>}

          <div className="a-grid a-grid--3">
            <section className="a-panel">
              <div className="a-panel__head">
                <h2 className="a-panel__title">Raise budget</h2>
                <span className="a-panel__meta">routine</span>
              </div>
              <p className="a-note">
                Sets the total ceiling. Raising it does not clear a trip — that is Reset.
              </p>
              <div className="a-form">
                <label className="a-field">
                  New total budget (USD)
                  <input
                    className="a-input"
                    placeholder="100.00"
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                  />
                </label>
                <button className="a-btn" onClick={() => void onSetBudget()}>
                  Set budget
                </button>
              </div>
            </section>

            <section className="a-panel">
              <div className="a-panel__head">
                <h2 className="a-panel__title">Trip</h2>
                <span className="a-panel__meta">stops all new spend</span>
              </div>
              <p className="a-note">
                Every new task is refused from the moment this lands. Running work is not touched;
                running GPUs keep billing until they stop themselves.
              </p>
              <div className="a-form">
                <label className="a-field">
                  Reason (recorded)
                  <input
                    className="a-input"
                    placeholder="why you are pulling the switch"
                    value={tripReason}
                    onChange={(e) => setTripReason(e.target.value)}
                  />
                </label>
                <button className="a-btn a-btn--danger" onClick={() => void onTrip()}>
                  Trip now
                </button>
              </div>
            </section>

            <section className="a-panel">
              <div className="a-panel__head">
                <h2 className="a-panel__title">Reset</h2>
                <span className="a-panel__meta">clears the trip</span>
              </div>
              <p className="a-note">
                Arms the switch again with a new budget. Only do this once whatever tripped it has
                been dealt with.
              </p>
              <div className="a-form">
                <label className="a-field">
                  New budget (USD)
                  <input
                    className="a-input"
                    placeholder="100.00"
                    value={resetBudget}
                    onChange={(e) => setResetBudget(e.target.value)}
                  />
                </label>
                <button className="a-btn a-btn--danger" onClick={() => void onReset()}>
                  Reset
                </button>
              </div>
            </section>
          </div>

          <section className="a-panel">
            <div className="a-panel__head">
              <h2 className="a-panel__title">Inputs</h2>
              <span className="a-panel__meta">what the budget is computed from</span>
            </div>
            <div className="a-scroll">
              <table className="a-table">
                <tbody>
                  {Object.entries(inputs ?? {})
                    .filter(([k, v]) => !(RESERVED_INPUTS.has(k) && !v))
                    .map(([k, v]) => (
                    <tr key={k}>
                      <td className="a-table__dim">{k}</td>
                      <td className="a-table__hi">{v === null || v === undefined ? "—" : String(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="a-panel" data-testid="killswitch-events">
            <div className="a-panel__head">
              <h2 className="a-panel__title">Event log</h2>
              <span className="a-panel__meta">trips, resets and budget changes, newest first</span>
            </div>
            {events === null ? (
              <p className="a-empty">no answer from KillSwitch</p>
            ) : events.length === 0 ? (
              <p className="a-empty">no events yet</p>
            ) : (
              <table className="a-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Event</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id}>
                      <td className="a-table__dim">{formatWhen(e.at)}</td>
                      <td className={e.kind === "TRIPPED" ? "a-table__hi a-tile__value--red" : "a-table__hi"}>{e.kind}</td>
                      <td className="a-table__dim">{eventDetail(e)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </>
  );
}

/** One line for an event's details: the reason of a trip, the budget of a
 * reset or a budget change, else the raw fields. */
function eventDetail(e: KillSwitchEvent): string {
  const d = e.details ?? {};
  if (typeof d.reason === "string") return d.reason;
  if (typeof d.budget_total_minor === "number") return `budget ${formatMinor(d.budget_total_minor, "USD_CENTS")}`;
  const parts = Object.entries(d).map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : String(v)}`);
  return parts.length > 0 ? parts.join(" ") : "—";
}
